import { useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { AuthenticateWithRedirectCallback, useAuth } from "@clerk/react"
import { requireLogin, type LoginMode } from "@/lib/authGate"
import { Beam } from "@/features/nira2/ds/Beam"
import "./auth-route.css"

/** A same-origin path from Clerk's redirect_url (absolute or relative); anything else is the research page. */
export function safeTarget(raw: string | null): string {
  if (!raw) return "/ask"
  try {
    const url = new URL(raw, window.location.origin)
    if (url.origin !== window.location.origin || url.pathname.startsWith("/auth/")) return "/ask"
    return `${url.pathname}${url.search}${url.hash}` || "/ask"
  } catch {
    return "/ask"
  }
}

function Finishing({ children }: { children?: React.ReactNode }) {
  return (
    <main className="na-auth-finish" role="status">
      <Beam state="working" size="hero" label="Signing you in" />
      <p>Signing you in…</p>
      {children}
    </main>
  )
}

function OpenDialog({ mode, target }: { mode: LoginMode; target: string }) {
  useEffect(() => {
    requireLogin("login_required", target === "/ask" ? null : target, mode)
  }, [mode, target])
  return <Navigate to="/ask" replace />
}

/**
 * Clerk's sign-in and sign-up URLs. Nira signs in in a dialog over the page (LoginModal), so these
 * routes only finish a Google sign-in (Clerk returns to /auth/login#/sso-callback) or hand a visitor
 * sent here over to that dialog. They never show a sign-in form of their own, so there is nothing
 * to bounce between.
 */
export function AuthRoute({ mode }: { mode: LoginMode }) {
  const { hash, pathname, search } = useLocation()
  const { isLoaded, isSignedIn } = useAuth()
  // Clerk puts where the reader was going in the hash query (#/sso-callback?sign_in_fallback_redirect_url=...).
  const hashQuery = new URLSearchParams(hash.split("?")[1] ?? "")
  const target = safeTarget(
    new URLSearchParams(search).get("redirect_url") ??
      hashQuery.get("sign_in_fallback_redirect_url") ??
      hashQuery.get("action_complete_redirect_url"),
  )
  if (hash.includes("sso-callback") || pathname.endsWith("/sso-callback")) {
    return (
      <Finishing>
        {/* Its own sign-in and sign-up URLs: without them it falls back to Clerk's hosted pages. */}
        <AuthenticateWithRedirectCallback
          signInUrl="/auth/login"
          signUpUrl="/auth/signup"
          signInFallbackRedirectUrl={target}
          signUpFallbackRedirectUrl={target}
        />
      </Finishing>
    )
  }
  if (!isLoaded) return <Finishing />
  if (isSignedIn) return <Navigate to={target} replace />
  return <OpenDialog mode={mode} target={target} />
}
